import '@/styles/index.css';
import 'react-toastify/dist/ReactToastify.css';
import 'yet-another-react-lightbox/styles.css';
import 'react-quill/dist/quill.snow.css';
import 'quill-mention/dist/quill.mention.css';

import { NuqsAdapter } from 'nuqs/adapters/react';
import { memo, useEffect, useRef } from 'react';
import { BrowserRouter } from 'react-router';
import ReactQueryProvider from './react-query-provider';
import ToastProvider from './toast-provider';
import TooltipProvider from './tooltip-provider';
import { initSentry } from '@/features/sentry/utils/sentry';
import SentryErrorBoundary from '../sentry/sentry-error-boundary';
import ModalContainer from '../ui/modal-container';
import { AuthContextProvider } from '@/features/auth/_shared/contexts/auth.context';
import SocketEventListenersProvider from './websocket/socket-event-listener-provider';
import ResponsiveProvider from './responsive-provider';
import SocketContextProvider from '@/websockets/contexts/socket.context';
import AppRoutes from '@/app/app.route';
import ModalRootProvider from '@/features/_shared/components/providers/modal-root-provider';
import SplashScreen from '@/features/_shared/components/splash-screen';
import NotificationProvider from '@/features/notifications/components/notification-provider/notification-provider';

function AppProvider() {
  // Guard against multiple Sentry initializations (e.g., in React StrictMode)
  const sentryInitializedRef = useRef(false);

  useEffect(() => {
    // Initialize Sentry only once, even in React StrictMode
    if (!sentryInitializedRef.current) {
      initSentry();
      sentryInitializedRef.current = true;
    }
  }, []);

  return (
    <SentryErrorBoundary>
      <ReactQueryProvider>
        <NuqsAdapter>
          <BrowserRouter>
            <ResponsiveProvider>
              <AuthContextProvider>
                <NotificationProvider>
                  <SocketContextProvider>
                    <SocketEventListenersProvider>
                      <SplashScreen>
                        <TooltipProvider>
                          <ModalRootProvider>
                            <AppRoutes />
                          </ModalRootProvider>
                          <ModalContainer />
                        </TooltipProvider>
                      </SplashScreen>
                    </SocketEventListenersProvider>
                  </SocketContextProvider>
                </NotificationProvider>
              </AuthContextProvider>
            </ResponsiveProvider>
          </BrowserRouter>
        </NuqsAdapter>
      </ReactQueryProvider>
      <ToastProvider />
    </SentryErrorBoundary>
  );
}

// Memoize to prevent unnecessary re-renders of the entire app tree
export default memo(AppProvider);
